/**
 * The campaign pages' route exports (G2 test d, ADR 9010/9041/9048) — `docs/adr/6017`.
 *
 *   history   one row per campaign simulation of the run: the question as authored and its state,
 *             every page of the read (a run keeps at most 30 per market, ADR 6041)
 *   results   one row per figure: each alternative's measures, then each alternative less the
 *             comparator, per channel — the median, the 80% bounds, the currency, or the reason it
 *             is absent; the gate and the refusal on every row of their alternative
 *
 * No rank column: the pages compare alternatives on realised margin (D4) and never rank them, so
 * the file carries what the comparison rests on, not an order. An empty file while the read is not
 * pinned or the link names no run or simulation, the filters saying which. No chart import: the
 * manifest imports this file.
 */

import { callOperation } from '@/api';
import {
  CAMPAIGN_RESULTS_READ, CAMPAIGN_SIMULATIONS_READ, campaignResultsOf, campaignSimulationsOf, type CampaignResults, type CampaignResultsRead,
  type CampaignSimulation, type CampaignSimulationsRead, type MeasureLine,
} from '@/api/pending/campaigns';
import {
  buildVersion, NO_PRINCIPAL, NOT_PUBLISHED, type ExportCell, type ExportColumn, type ExportRequest, type Provenance, type ProvenanceFilter,
} from '@/export';
import { PERSON_NAME_DISPLAY, personExportCell } from '@/planning';

import type { ExportScope } from '../../manifest';

import { readCampaignDrill, readCampaignScope } from './scope';

export const HISTORY_EXPORT_ID = 'inseason.campaigns.history';
export const RESULTS_EXPORT_ID = 'inseason.campaigns.results';

/** The most pages a file walks; past it the export fails loudly rather than truncating. */
const MAX_PAGES = 20;

const num = (value: number | null): ExportCell => (value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value });
const text = (value: string | null): ExportCell => (value === null || value === '' ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value });

function request(meta: {
  readonly id: string;
  readonly titleKey: string;
  readonly columns: readonly ExportColumn[];
  readonly rows: readonly (readonly ExportCell[])[];
  readonly filters: readonly ProvenanceFilter[];
  readonly timeWindow: Provenance['timeWindow'];
  readonly scenario: Provenance['scenario'];
  readonly definition: string;
}): ExportRequest {
  return {
    id: meta.id,
    titleKey: meta.titleKey,
    columns: meta.columns,
    rows: () => meta.rows,
    rowCount: meta.rows.length,
    provenance: {
      filters: meta.filters,
      hierarchyNode: NOT_PUBLISHED,
      timeWindow: meta.timeWindow,
      scenario: meta.scenario,
      planVersionId: NOT_PUBLISHED,
      measureDefinitions: [{ measure: meta.id, definition: meta.definition, registerVersion: '' }],
      generatedAt: new Date().toISOString(),
      generatedBy: NO_PRINCIPAL,
      platformVersion: buildVersion(),
    },
    perimeter: { filters: meta.filters, sort: [], visibleColumns: meta.columns.map((column) => column.key), drillPath: [], densityMode: 'compact' },
  };
}

const HISTORY_DEFINITION = 'bata-platform ADR 6065: the campaign simulations asked of one base run, newest first. Each is one envelope (inclusive dates, channel, banner, doors, articles as include rows less exclude rows) and two to six alternatives, each a governed mechanic with its depth or buy/pay quantities; never "no promotion" (D5). Advisory (bata-reqs issue 33, D3): nothing the platform runs reads a campaign simulation, and nothing in it can be applied, approved or exported as an action.';

const RESULTS_DEFINITION = 'bata-platform ADR 6065/6066: every figure is the median of the simulation’s draws with its 80% interval (low = 10th percentile, high = 90th). section=alternative: the alternative’s own figures against the history’s average promotion mix applied to the base run’s forecast; section=difference: the alternative less the comparator, draw by draw (units = incremental units, net_sales = incremental net). Alternatives are compared on realised margin only (D4), which covers the window, the pull-forward and the markdown exposure (ADR 6069); the service verdict (pass/breach against inseason.pull.service_target) is a constraint and never part of the comparison (ADR 6068); unmeasured stock positions are left out, never read as zero; units and net sales are shown, never compared. A null figure is absent with absent_reason, never zero; until the article-day panel lands every money figure is absent (panel_not_landed). Advisory (bata-reqs issue 33, D3): nothing the platform runs reads a campaign simulation, and nothing in it can be applied, approved or exported as an action.';

// ── history ──────────────────────────────────────────────────────────────────

export const HISTORY_COLUMNS: readonly ExportColumn[] = [
  { key: 'simulation_id', headerKey: 'inseason.campaigns.column.simulation', descriptionKey: 'inseason.campaigns.columnGuide.simulation', kind: 'text' },
  { key: 'base_run_id', headerKey: 'inseason.campaigns.column.baseRun', descriptionKey: 'inseason.campaigns.columnGuide.baseRun', kind: 'text' },
  { key: 'country_key', headerKey: 'inseason.campaigns.column.market', descriptionKey: 'inseason.campaigns.columnGuide.market', kind: 'text' },
  { key: 'scenario_id', headerKey: 'inseason.campaigns.column.scenario', descriptionKey: 'inseason.campaigns.columnGuide.scenario', kind: 'text' },
  { key: 'fit_id', headerKey: 'inseason.campaigns.column.fit', descriptionKey: 'inseason.campaigns.columnGuide.fit', kind: 'text' },
  { key: 'starts_on', headerKey: 'inseason.campaigns.column.startsOn', descriptionKey: 'inseason.campaigns.columnGuide.startsOn', kind: 'date' },
  { key: 'ends_on', headerKey: 'inseason.campaigns.column.endsOn', descriptionKey: 'inseason.campaigns.columnGuide.endsOn', kind: 'date' },
  { key: 'channel', headerKey: 'inseason.campaigns.column.channel', descriptionKey: 'inseason.campaigns.columnGuide.channel', kind: 'text' },
  { key: 'banner', headerKey: 'inseason.campaigns.column.banner', descriptionKey: 'inseason.campaigns.columnGuide.banner', kind: 'text' },
  { key: 'stores', headerKey: 'inseason.campaigns.column.stores', descriptionKey: 'inseason.campaigns.columnGuide.stores', kind: 'text' },
  { key: 'include', headerKey: 'inseason.campaigns.column.include', descriptionKey: 'inseason.campaigns.columnGuide.include', kind: 'text' },
  { key: 'exclude', headerKey: 'inseason.campaigns.column.exclude', descriptionKey: 'inseason.campaigns.columnGuide.exclude', kind: 'text' },
  { key: 'alternatives', headerKey: 'inseason.campaigns.column.alternatives', descriptionKey: 'inseason.campaigns.columnGuide.alternatives', kind: 'text' },
  { key: 'comparator_index', headerKey: 'inseason.campaigns.column.comparator', descriptionKey: 'inseason.campaigns.columnGuide.comparator', kind: 'number' },
  { key: 'state', headerKey: 'inseason.campaigns.column.state', descriptionKey: 'inseason.campaigns.columnGuide.state', kind: 'text' },
  { key: 'failure_code', headerKey: 'inseason.campaigns.column.failure', descriptionKey: 'inseason.campaigns.columnGuide.failure', kind: 'text' },
  // Who asked, by name (bata-platform ADR 6198): «Sistema» or the reason in words in the workbook; the principal stays in its own column.
  { key: 'requested_by_name', headerKey: 'inseason.campaigns.column.requestedBy', descriptionKey: 'inseason.campaigns.columnGuide.requestedByName', kind: 'label', display: PERSON_NAME_DISPLAY },
  { key: 'requested_by', headerKey: 'inseason.campaigns.column.requestedById', descriptionKey: 'inseason.campaigns.columnGuide.requestedBy', kind: 'text' },
  { key: 'requested_at', headerKey: 'inseason.campaigns.column.requestedAt', descriptionKey: 'inseason.campaigns.columnGuide.requestedAt', kind: 'text' },
];

const storesText = (row: CampaignSimulation): string => (row.stores.kind === 'clusters' ? ['clusters', row.stores.clusters.join('|')].join(':')
  : row.stores.kind === 'districts' ? ['districts', row.stores.districts.join('|')].join(':')
    : row.stores.kind === 'doors' ? ['doors', row.stores.doors.join('|')].join(':') : 'all');
const rowsText = (rows: CampaignSimulation['include']): string => rows.map((row) => [row.kindRaw, row.value].join(':')).join('|');
const alternativeText = (row: CampaignSimulation): string => row.alternatives
  .map((alternative) => [alternative.index, alternative.label, alternative.mechanic.raw, alternative.depthPct ?? '', alternative.buy ?? '', alternative.pay ?? ''].join(':'))
  .join('|');

export function historyRow(row: CampaignSimulation): readonly ExportCell[] {
  return [
    text(row.id), text(row.baseRunId), text(row.market), text(row.scenarioId), text(row.fitId),
    { kind: 'date', iso: row.startsOn }, { kind: 'date', iso: row.endsOn }, text(row.channel), text(row.banner),
    text(storesText(row)), text(rowsText(row.include)), text(rowsText(row.exclude)), text(alternativeText(row)), num(row.comparator),
    text(row.state), text(row.failureCode),
    personExportCell(row.requestedBy, row.requestedByName, row.requestedByNameAbsentReason), text(row.requestedBy), text(row.requestedAt),
  ];
}

const runFilter = (runId: string | null): ProvenanceFilter => (runId === null
  ? { field: 'run_id', operator: 'unset', value: '' }
  : { field: 'run_id', operator: 'equals', value: runId });

/** The table's own download: the rows the planner sees, as the route's file. */
export function campaignHistoryRequest(rows: readonly CampaignSimulation[], runId: string): ExportRequest {
  return request({
    id: HISTORY_EXPORT_ID, titleKey: 'inseason.campaigns.history.exportTitle', columns: HISTORY_COLUMNS, rows: rows.map(historyRow),
    filters: [runFilter(runId)], timeWindow: NOT_PUBLISHED, scenario: NOT_PUBLISHED, definition: HISTORY_DEFINITION,
  });
}

/** The route's file: every campaign simulation of the run on the link, every page by cursor. */
export async function campaignHistoryExport(given?: ExportScope, read: CampaignSimulationsRead | null = CAMPAIGN_SIMULATIONS_READ): Promise<ExportRequest> {
  const { runId } = readCampaignScope(given?.search ?? '');
  const base = { id: HISTORY_EXPORT_ID, titleKey: 'inseason.campaigns.history.exportTitle', columns: HISTORY_COLUMNS, filters: [runFilter(runId)], timeWindow: NOT_PUBLISHED, scenario: NOT_PUBLISHED, definition: HISTORY_DEFINITION };
  if (read === null || runId === null) return request({ ...base, rows: [] });
  const rows: CampaignSimulation[] = [];
  let cursor: string | null = null;
  for (let pages = 0; ; pages += 1) {
    if (pages >= MAX_PAGES) throw new Error(`campaign history export: more than ${MAX_PAGES} pages for run ${runId}`);
    const page: Awaited<ReturnType<typeof callOperation<CampaignSimulationsRead>>> = await callOperation(read, { query: { run_id: runId, ...(cursor === null ? {} : { cursor }) } }, {});
    const answered = campaignSimulationsOf(page);
    rows.push(...answered.items);
    if (answered.next === null) break;
    cursor = answered.next;
  }
  return request({ ...base, rows: rows.map(historyRow) });
}

// ── results ──────────────────────────────────────────────────────────────────

export const RESULT_COLUMNS: readonly ExportColumn[] = [
  { key: 'section', headerKey: 'inseason.campaigns.column.section', kind: 'text', descriptionKey: 'inseason.campaigns.columnGuide.section' },
  { key: 'alternative_index', headerKey: 'inseason.campaigns.column.alternativeIndex', descriptionKey: 'inseason.campaigns.columnGuide.alternativeIndex', kind: 'number' },
  { key: 'alternative_label', headerKey: 'inseason.campaigns.column.alternative', descriptionKey: 'inseason.campaigns.columnGuide.alternative', kind: 'text' },
  { key: 'mechanic_code', headerKey: 'inseason.campaigns.column.mechanic', descriptionKey: 'inseason.campaigns.columnGuide.mechanic', kind: 'text' },
  { key: 'depth_pct', headerKey: 'inseason.campaigns.column.depth', descriptionKey: 'inseason.campaigns.columnGuide.depth', kind: 'number' },
  { key: 'buy_quantity', headerKey: 'inseason.campaigns.column.buy', descriptionKey: 'inseason.campaigns.columnGuide.buy', kind: 'number' },
  { key: 'pay_quantity', headerKey: 'inseason.campaigns.column.pay', descriptionKey: 'inseason.campaigns.columnGuide.pay', kind: 'number' },
  { key: 'comparator_index', headerKey: 'inseason.campaigns.column.comparator', descriptionKey: 'inseason.campaigns.columnGuide.comparator', kind: 'number' },
  { key: 'channel_key', headerKey: 'inseason.campaigns.column.channel', descriptionKey: 'inseason.campaigns.columnGuide.channelKey', kind: 'text' },
  { key: 'measure', headerKey: 'inseason.campaigns.column.measure', descriptionKey: 'inseason.campaigns.columnGuide.measure', kind: 'text' },
  { key: 'estimate', headerKey: 'inseason.campaigns.column.estimate', kind: 'number', descriptionKey: 'inseason.campaigns.columnGuide.estimate' },
  { key: 'low', headerKey: 'inseason.campaigns.column.low', kind: 'number', descriptionKey: 'inseason.campaigns.columnGuide.bounds' },
  { key: 'high', headerKey: 'inseason.campaigns.column.high', kind: 'number', descriptionKey: 'inseason.campaigns.columnGuide.bounds' },
  { key: 'currency_code', headerKey: 'inseason.campaigns.column.currency', descriptionKey: 'inseason.campaigns.columnGuide.currencyCode', kind: 'text' },
  { key: 'absent_reason', headerKey: 'inseason.campaigns.column.absentReason', descriptionKey: 'inseason.campaigns.columnGuide.absentReason', kind: 'text' },
  { key: 'probability_higher_realised_margin', headerKey: 'inseason.campaigns.column.probability', kind: 'number', descriptionKey: 'inseason.campaigns.columnGuide.probability' },
  { key: 'gate', headerKey: 'inseason.campaigns.column.gate', descriptionKey: 'inseason.campaigns.columnGuide.gate', kind: 'text' },
  { key: 'refusal_reason', headerKey: 'inseason.campaigns.column.refusal', descriptionKey: 'inseason.campaigns.columnGuide.refusal', kind: 'text' },
];

function lineCells(line: MeasureLine): readonly ExportCell[] {
  return [
    text(line.channel), text(line.measureRaw), num(line.value?.estimate ?? null), num(line.value?.low ?? null), num(line.value?.high ?? null),
    text(line.currency), text(line.absentReason),
  ];
}

/** Every figure of an answer as rows: the alternatives' own, then the differences. Pure. */
export function resultRows(results: CampaignResults): readonly (readonly ExportCell[])[] {
  const byIndex = new Map(results.alternatives.map((alternative) => [alternative.index, alternative]));
  const own = results.alternatives.flatMap((alternative) => alternative.measures.map((line) => [
    text('alternative'), num(alternative.index), text(alternative.label), text(alternative.mechanic.raw), num(alternative.depthPct),
    num(alternative.buy), num(alternative.pay), num(results.comparator), ...lineCells(line), num(null), text(alternative.gateRaw),
    text(alternative.refusal?.reason ?? null),
  ]));
  const differences = results.differences.flatMap((difference) => {
    const alternative = byIndex.get(difference.alternative);
    return difference.measures.map((line) => [
      text('difference'), num(difference.alternative), text(alternative?.label ?? null), text(alternative?.mechanic.raw ?? null),
      num(alternative?.depthPct ?? null), num(alternative?.buy ?? null), num(alternative?.pay ?? null), num(difference.comparator),
      ...lineCells(line), num(difference.probabilityHigherRealisedMargin), text(alternative?.gateRaw ?? null),
      text(alternative?.refusal?.reason ?? null),
    ]);
  });
  return [...own, ...differences];
}

/** The route's file: the simulation the path names, every figure. */
export async function campaignResultsExport(given?: ExportScope, read: CampaignResultsRead | null = CAMPAIGN_RESULTS_READ): Promise<ExportRequest> {
  const simulationId = readCampaignDrill(given?.path ?? '');
  const filters: readonly ProvenanceFilter[] = [simulationId === null
    ? { field: 'simulation_id', operator: 'unset', value: '' }
    : { field: 'simulation_id', operator: 'equals', value: simulationId }];
  const base = { id: RESULTS_EXPORT_ID, titleKey: 'inseason.campaigns.results.exportTitle', columns: RESULT_COLUMNS, filters, definition: RESULTS_DEFINITION };
  if (read === null || simulationId === null) return request({ ...base, rows: [], timeWindow: NOT_PUBLISHED, scenario: NOT_PUBLISHED });
  const results = campaignResultsOf(await callOperation(read, { path: { simulation_id: simulationId } }, {}));
  return request({
    ...base,
    rows: resultRows(results),
    timeWindow: { from: results.simulation.startsOn, to: results.simulation.endsOn },
    scenario: results.simulation.scenarioId,
  });
}
